import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { IconButton, TableCell, TableRow } from '@mui/material'
import classNames from 'classnames'
import { DateUtils } from '@pbt/pbt-ui-components'

import {
  CONVERSATION_MESSAGES_FIRST_FETCH_COUNT,
  ConversationStatus,
  MessageStatus,
} from '~/constants/communications'
import { fetchConversationMessagesList } from '~/store/actions/conversations'
import { getChatMessages } from '~/store/reducers/conversationMessages'
import type { Conversation, ConversationMessage } from '~/types'

import SubjectCell from './SubjectCell'

import styles from './CommunicationRow.module.scss'

const CommunicationRow = ({
  conversation,
  isLoading,
  LoadingSkeleton,
}: {
  LoadingSkeleton: React.ReactElement
  conversation: Conversation
  isLoading: boolean
}) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const messages: ConversationMessage[] = useSelector(
    getChatMessages(conversation?.messages || []),
  )
  const isExpandable = messages.length > 1

  const { id, title, status } = conversation

  const isUnread = status === ConversationStatus.UNREAD

  useEffect(() => {
    if (id) {
      dispatch(
        fetchConversationMessagesList(
          0,
          CONVERSATION_MESSAGES_FIRST_FETCH_COUNT,
          id,
        ),
      )
    }
  }, [id])

  if (isLoading) {
    return LoadingSkeleton
  }

  const [firstMessage] = messages
  const messageContent = firstMessage?.body || ''
  const isDraft = firstMessage?.status === MessageStatus.DRAFT

  return (
    <TableRow
      className={classNames(styles.tableRow, {
        [styles.unreadTableRow]: isUnread,
      })}
    >
      <TableCell className={styles.expandCell}>
        {isExpandable && (
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        )}
      </TableCell>
      <TableCell className={styles.tableCell}>
        {DateUtils.formatDate(
          conversation.lastMessageDate,
          false,
          '[full-date-time]',
        )}
      </TableCell>
      <SubjectCell
        id={id}
        isDraft={isDraft}
        isUnread={isUnread}
        message={messageContent}
        subject={title}
      />
      <TableCell className={styles.tableCell}>—</TableCell>
    </TableRow>
  )
}

export default CommunicationRow
